import { useCallback } from 'react';

import { useRouter } from 'expo-router';
import { logAddRecentItemToCartEvent } from 'features/order/utils/analytics';
import { useStoreContext } from 'features/store';

import { ILegacyCartEntry, MenuObject } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import { useCart } from 'hooks/use-cart';
import { useSwitchCartMethodDialog } from 'hooks/use-switch-cart-method-dialog';
import { CustomEventNames } from 'state/amplitude';
import { useMenuContext } from 'state/menu';
import { useGetMenuObjectLazyQuery } from 'state/menu/menu-options/hooks/use-get-menu-object-lazy-query';
import { useOrderContext } from 'state/order';
import { maybeMapCartEntryToMenuObjectIdentifier } from 'utils/menu';
import { routes } from 'utils/routing';

interface UseAddToCartProps {
  cartEntry: ILegacyCartEntry | null;
  amplitudeEvent?: CustomEventNames;
}

const useAddToCartRecentItem = ({ cartEntry, amplitudeEvent }: UseAddToCartProps) => {
  const { checkItemAvailability } = useMenuContext();
  const getMenuObjectLazyQuery = useGetMenuObjectLazyQuery();
  const router = useRouter();
  // TODO: BKPE-1695: Migrate Pending recent item to RTK
  const {
    recent: { setPendingRecentItem, setUnavailablePendingRecentItem },
  } = useOrderContext();
  const { addItemToCart } = useCart();
  const { store, isStoreOpenAndAvailable } = useStoreContext();
  const {
    SwitchCartMethodDialog,
    openSwitchCartMethod,
    shouldShowSwitchToMobileDialog,
    // @ts-ignore
  } = useSwitchCartMethodDialog();

  const handleAddToCart = useCallback(async () => {
    if (!cartEntry) {
      return;
    }
    if (isStoreOpenAndAvailable) {
      if (amplitudeEvent) {
        logAddRecentItemToCartEvent(cartEntry?.name ?? '', store);
      }
      addItemToCart(cartEntry);
    } else {
      const itemId = maybeMapCartEntryToMenuObjectIdentifier(cartEntry);

      if (!itemId) {
        setUnavailablePendingRecentItem(cartEntry);
        return;
      }

      // Since we don't have vendorConfigs for user uniquePurchasedItems,
      // and there is no a selected store at this point,
      // then we need to fetch this information.
      const data = await getMenuObjectLazyQuery(itemId);

      // TODO: Menu - Remove this cast when all menu types are auto-generated
      const menuObject = data as MenuObject;

      if (!menuObject || !('vendorConfigs' in menuObject)) {
        setUnavailablePendingRecentItem(cartEntry);
        return;
      }

      const cartEntryWithVendorConfigs = { ...cartEntry, vendorConfigs: menuObject.vendorConfigs };

      // Setting the pending recent item
      // The item will be added on the order context after selecting the store
      setPendingRecentItem(cartEntryWithVendorConfigs);
      router.navigate(routes.storeLocator);
    }
  }, [
    cartEntry,
    isStoreOpenAndAvailable,
    amplitudeEvent,
    addItemToCart,
    store,
    getMenuObjectLazyQuery,
    setPendingRecentItem,
    router,
    setUnavailablePendingRecentItem,
  ]);

  const addToCart = useCallback(async () => {
    if (shouldShowSwitchToMobileDialog) {
      openSwitchCartMethod({ onConfirm: handleAddToCart });
    } else {
      await handleAddToCart();
    }
  }, [handleAddToCart, openSwitchCartMethod, shouldShowSwitchToMobileDialog]);

  const addToCartIfAvailable = useCallback(
    async (cartEntry: ILegacyCartEntry) => {
      const itemId = maybeMapCartEntryToMenuObjectIdentifier(cartEntry);
      if (!itemId) {
        setUnavailablePendingRecentItem(cartEntry);
        return;
      }

      const { availabilityStatus } = await checkItemAvailability(itemId);

      if (availabilityStatus !== ItemAvailabilityStatus.AVAILABLE) {
        setUnavailablePendingRecentItem(cartEntry);
      } else {
        addToCart();
      }
    },
    [addToCart, checkItemAvailability, setUnavailablePendingRecentItem]
  );

  return {
    addToCart,
    addToCartIfAvailable,
    SwitchCartMethodDialog,
  };
};

export default useAddToCartRecentItem;
